import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  CameraIcon,
  BriefcaseIcon,
  HeartIcon,
  ArrowRightIcon,
  UserCircleIcon,
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  ArrowLeftIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const INTEREST_OPTIONS = [
  'Foodie', 'Travel', 'Reading', 'Music', 'Movies', 'Sports', 'Fitness', 'Gaming',
  'Photography', 'Art', 'Dancing', 'Hiking', 'Cooking', 'Technology', 'Fashion',
  'Politics', 'Startups', 'Investing', 'Yoga', 'Pets', 'Writing', 'Volunteering'
];

const HolaProfileSetup = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const hasPhoto = !!user?.photo;
  const hasProfession = !!user?.profession;
  const hasInterests = user?.interests?.length > 0;
  const completedCount = [hasPhoto, hasProfession, hasInterests].filter(Boolean).length;
  const isProfileComplete = completedCount === 3;

  const getInitialStep = () => {
    if (isProfileComplete) return null; // show summary
    if (!hasPhoto) return 1;
    if (!hasProfession) return 2;
    if (!hasInterests) return 3;
    return null;
  };

  const [editingStep, setEditingStep] = useState(getInitialStep);
  const [loading, setLoading] = useState(false);
  const [photo, setPhoto] = useState(user?.photo || null);
  const [profession, setProfession] = useState(user?.profession || '');
  const [interests, setInterests] = useState(user?.interests || []);
  const [error, setError] = useState('');

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Photo must be less than 5MB');
        return;
      }

      try {
        const compressed = await compressImage(file);
        setPhoto(compressed);
        setError('');
      } catch {
        setError('Failed to process photo. Please try another image.');
      }
    }
  };

  const toggleInterest = (interest) => {
    setInterests(prev =>
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  const handleSave = async () => {
    if (editingStep === 1 && !photo) {
      setError('Please upload a photo to continue');
      return;
    }
    if (editingStep === 2 && !profession.trim()) {
      setError('Please enter your profession');
      return;
    }
    if (editingStep === 3 && interests.length === 0) {
      setError('Please select at least one interest');
      return;
    }

    setError('');

    // If incomplete, advance to next pending step
    if (!isProfileComplete && editingStep !== null) {
      const nextPending = getNextPendingStep(editingStep);
      if (nextPending) {
        setEditingStep(nextPending);
        return;
      }
    }

    // All fields filled — save
    await saveProfile();
  };

  const getNextPendingStep = (currentStep) => {
    // Always check local state, not server state, since user may have filled fields without saving yet
    const checks = [
      { step: 1, done: !!photo },
      { step: 2, done: !!profession.trim() },
      { step: 3, done: interests.length > 0 },
    ];
    for (const c of checks) {
      if (!c.done && c.step > currentStep) return c.step;
    }
    for (const c of checks) {
      if (!c.done) return c.step;
    }
    return null; // all done
  };

  const saveProfile = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      let photoUrl = photo;

      if (photo && photo.startsWith('data:image/')) {
        const uploadRes = await axios.post(`${API_URL}/upload/screenshot`, {
          image: photo,
          transactionType: 'profile'
        }, { headers });
        photoUrl = uploadRes.data.url;
      }

      await axios.put(`${API_URL}/users/profile`, {
        photo: photoUrl,
        profession,
        interests
      }, { headers });

      await refreshUser();
      navigate('/dining-events');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ──────────── Summary View (complete or overview) ────────────

  const renderSummary = () => {
    const percentage = Math.round((completedCount / 3) * 100);
    const items = [
      { label: 'Photo', done: hasPhoto, step: 1, icon: CameraIcon },
      { label: 'Profession', done: hasProfession, step: 2, icon: BriefcaseIcon, value: user?.profession },
      { label: 'Interests', done: hasInterests, step: 3, icon: HeartIcon, value: user?.interests?.join(', ') },
    ];

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <div className="bg-white px-4 py-4 shadow-sm">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1 -ml-1 text-gray-500 hover:text-gray-700">
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Your Profile</h1>
          </div>
        </div>

        <div className="flex-1 px-4 py-8">
          <div className="max-w-md mx-auto">
            {/* Status Badge */}
            {isProfileComplete ? (
              <div className="flex items-center gap-3 mb-8 p-4 bg-green-50 border border-green-200 rounded-xl">
                <CheckCircleIcon className="h-8 w-8 text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">Profile Completed</p>
                  <p className="text-sm text-green-600">You're all set for Connect events</p>
                </div>
              </div>
            ) : (
              <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-amber-800">{percentage}% Completed</p>
                  <p className="text-sm text-amber-600">{3 - completedCount} pending</p>
                </div>
                <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all duration-300 rounded-full"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Profile Items */}
            <div className="space-y-3">
              {items.map(({ label, done, step, icon: Icon, value }) => (
                <div
                  key={step}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    done ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {step === 1 && done && user?.photo ? (
                      <img src={user.photo} alt="Profile" className="w-12 h-12 rounded-xl object-cover" />
                    ) : (
                      <Icon className={`h-6 w-6 ${done ? 'text-green-600' : 'text-gray-400'}`} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{label}</span>
                      {done ? (
                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Done</span>
                      ) : (
                        <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Pending</span>
                      )}
                    </div>
                    {done && value && (
                      <p className="text-sm text-gray-500 truncate mt-0.5">{value}</p>
                    )}
                  </div>

                  {/* Action */}
                  <button
                    onClick={() => setEditingStep(step)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium flex-shrink-0 ${
                      done
                        ? 'text-gray-600 hover:bg-gray-100'
                        : 'text-white bg-gray-900 hover:bg-gray-800'
                    }`}
                  >
                    {done ? (
                      <>
                        <PencilIcon className="h-3.5 w-3.5" />
                        Update
                      </>
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>
              ))}
            </div>

            {/* Complete pending items CTA */}
            {!isProfileComplete && (
              <button
                onClick={() => {
                  const first = [1, 2, 3].find(s =>
                    (s === 1 && !hasPhoto) || (s === 2 && !hasProfession) || (s === 3 && !hasInterests)
                  );
                  setEditingStep(first);
                }}
                className="w-full mt-6 flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-xl font-semibold text-lg hover:bg-gray-800 transition-colors"
              >
                Complete Profile
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ──────────── Step Editing Views ────────────

  const renderEditStep = () => {
    switch (editingStep) {
      case 1:
        return (
          <div className="text-center">
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary-500 to-rose-500 rounded-2xl flex items-center justify-center mb-4">
                <CameraIcon className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {hasPhoto ? 'Update Your Photo' : 'Add Your Photo'}
              </h2>
              <p className="text-gray-600">Help others recognize you at Connect events</p>
            </div>

            <div className="mb-8">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoChange}
                accept="image/*"
                className="hidden"
              />

              <div
                onClick={handlePhotoClick}
                className="relative w-48 h-48 mx-auto rounded-full border-4 border-dashed border-gray-300 hover:border-primary-500 cursor-pointer transition-colors overflow-hidden bg-gray-50 flex items-center justify-center"
              >
                {photo ? (
                  <img
                    src={photo}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center">
                    <UserCircleIcon className="h-20 w-20 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Tap to upload photo</p>
                  </div>
                )}

                {photo && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPhoto(null);
                    }}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}

                <div className="absolute bottom-2 right-2 w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center shadow-lg">
                  <CameraIcon className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="text-center">
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary-500 to-rose-500 rounded-2xl flex items-center justify-center mb-4">
                <BriefcaseIcon className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {hasProfession ? 'Update Your Profession' : "What's Your Profession?"}
              </h2>
              <p className="text-gray-600">Let others know what you do</p>
            </div>

            <div className="max-w-sm mx-auto">
              <input
                type="text"
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                placeholder="e.g., Software Engineer, Doctor, Designer..."
                className="w-full px-4 py-4 text-lg text-center border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-0 outline-none transition-colors"
                maxLength={50}
              />
              <p className="text-sm text-gray-400 mt-2">{profession.length}/50 characters</p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="text-center">
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary-500 to-rose-500 rounded-2xl flex items-center justify-center mb-4">
                <HeartIcon className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {hasInterests ? 'Update Your Interests' : 'Select Your Interests'}
              </h2>
              <p className="text-gray-600">Pick topics you love to talk about</p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 max-w-md mx-auto">
              {INTEREST_OPTIONS.map((interest) => (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    interests.includes(interest)
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {interests.includes(interest) && <CheckIcon className="h-3 w-3" />}
                    {interest}
                  </span>
                </button>
              ))}
            </div>

            <p className="text-sm text-gray-500 mt-4">
              {interests.length} selected {interests.length > 0 && `• ${interests.join(', ')}`}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // ──────────── Edit Mode Layout ────────────

  if (editingStep !== null) {
    const isEditing = isProfileComplete; // editing existing vs completing new
    const buttonLabel = isEditing
      ? 'Save'
      : (getNextPendingStep(editingStep) ? 'Continue' : 'Complete Profile');

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Top Bar */}
        <div className="bg-white px-4 py-4 shadow-sm">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <button
              onClick={() => setEditingStep(null)}
              className="p-1 -ml-1 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-600">
              {isEditing ? 'Edit Profile' : `Step ${editingStep} of 3`}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col justify-center px-4 py-8">
          {renderEditStep()}

          {error && (
            <div className="max-w-sm mx-auto mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-center text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white px-4 py-6 shadow-lg">
          <div className="max-w-md mx-auto">
            <button
              onClick={isEditing ? saveProfile : handleSave}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-xl font-semibold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  Saving...
                </>
              ) : (
                <>
                  {buttonLabel}
                  <ArrowRightIcon className="h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ──────────── Default: Summary View ────────────
  return renderSummary();
};

export default HolaProfileSetup;
